import { createContext, useContext, useEffect, useMemo, useState } from "react";
import usePortalConfig from "@/contexts/portal-config/portal-config-context";

interface BannerContextType {
  visible: boolean;
  toggleVisible: () => void;
  bannerMessage: string | null;
}

const BannerContext = createContext<BannerContextType | null>(null);

interface BannerStorageConfig {
  lastMessage: string;
  lastClosed: string;
}

const BANNER_KEY = "banner";
const BANNER_REFRESH_HOURS = 12;

export function BannerProvider({ children }: { children: React.ReactNode }) {
  const config = usePortalConfig();
  const [visible, setVisible] = useState<boolean>(false);

  const isTimeOrContentRefresh = useMemo(() => {
    const message = config?.bannerMessage;
    if (!message) return false;

    const lastJson = localStorage.getItem(BANNER_KEY);
    if (lastJson == null) return true; // never dismissed

    const last: BannerStorageConfig = JSON.parse(lastJson);

    if (last.lastMessage !== message) {
      localStorage.removeItem(BANNER_KEY);
      return true;
    }

    const hours =
      (Date.now() - new Date(last.lastClosed).getTime()) / (1000 * 60 * 60);

    if (hours > BANNER_REFRESH_HOURS) {
      localStorage.removeItem(BANNER_KEY);
      return true;
    }

    return false;
  }, [config?.bannerMessage]);

  useEffect(() => {
    setVisible((config?.bannerMessage?.length ?? 0) > 0 && isTimeOrContentRefresh);
  }, [config]);

  const toggleVisible = () => {
    const bannerStorageConfig: BannerStorageConfig = {
      lastMessage: config?.bannerMessage!,
      lastClosed: new Date().toUTCString(),
    }

    localStorage.setItem(BANNER_KEY, JSON.stringify(bannerStorageConfig));

    setVisible(false);
  };

  return (
    <BannerContext.Provider
      value={{
        visible: visible,
        toggleVisible,
        bannerMessage: config?.bannerMessage || "",
      }}
    >
      {children}
    </BannerContext.Provider>
  );
}

export const useBanner = () => {
  const context = useContext(BannerContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
